import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { useWorkspace } from './WorkspaceContext';
import { toast } from 'sonner';

// ============================================
// TYPES
// ============================================

interface SubscriptionTier {
  id: string;
  name: string;
  description: string;
  price_aed_monthly: number;
  price_usd_monthly: number;
  stripe_price_id: string | null;
  // New simplified mission-based limits
  missions_limit: number;  // Missions per month (-1 = unlimited)
  concurrent_missions_limit: number;  // Max concurrent (-1 = unlimited)
  credits_limit: number; // Credits per month (-1 = unlimited)
  // Legacy limits (kept for compatibility, may be deprecated)
  steps_per_month: number;
  searches_per_month: number;
  protocols_limit: number;
  tracks_limit: number;
  connectors_limit: number;
  memory_days: number;
  foreach_batch_limit: number;
  max_users: number;
  included_users: number;
  whatsapp_enabled: boolean;
  gsuite_read_only: boolean;
  gsuite_write: boolean;
  custom_connectors: boolean;
  sdk_access: boolean;
  priority_support: boolean;
  available_on_ios: boolean;
  available_on_web: boolean;
}

interface SubscriptionUsage {
  steps_used: number;
  searches_used: number;
  protocols_active: number;
  tracks_active: number;
  period_start: string;
  period_end: string;
}

interface Subscription {
  id: string;
  tier_id: string;
  status: 'active' | 'past_due' | 'canceled' | 'paused';
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  seat_count: number;
  leader_count: number;  // For Team tier
  is_trial: boolean;
  trial_ends_at: string | null;
  trial_days_remaining?: number; // Computed from trial_ends_at
}

/**
 * A single enrichment addon subscription — e.g. Proxycurl Person at $100/mo.
 * Workspaces can have multiple of these active at once, stacked on top of
 * their base tier subscription. Identified by a tier_id with the 'enricher_'
 * prefix. See plan: Phase 0 billing prep for the full rationale.
 */
interface EnricherSubscription {
  id: string;
  tier_id: string;                    // e.g. 'enricher_proxycurl_person_starter'
  status: 'active' | 'past_due' | 'canceled' | 'paused';
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  tier_name?: string | null;          // From joined subscription_tiers row
  monthly_price_usd?: number | null;  // From joined subscription_tiers.price_usd_monthly
}

interface Entitlements {
  // Current subscription
  subscription: Subscription | null;
  tier: SubscriptionTier | null;
  usage: SubscriptionUsage | null;
  
  // NEW: Mission-based limits (primary pricing model)
  missionsPerMonth: number;  // -1 = unlimited
  missionsUsed: number;
  missionsRemaining: number;
  concurrentMissions: number;  // -1 = unlimited
  concurrentRunning: number;
  isTeamTier: boolean;
  leaderCount: number;  // For team tier, affects multiplier
  isTrial: boolean;
  trialEndsAt: string | null;
  
  // Computed limits (legacy - kept for compatibility)
  stepsLimit: number;
  stepsUsed: number;
  stepsRemaining: number;
  searchesLimit: number;
  searchesUsed: number;
  searchesRemaining: number;
  creditsLimit: number;
  creditsUsed: number;
  creditsRemaining: number;
  
  // Feature flags
  canWriteGsuite: boolean;
  canUseCustomConnectors: boolean;
  canUseSdk: boolean;
  hasPrioritySupport: boolean;
  
  // Legacy compatibility
  modules: string[];
  playbooks: string[];
  seatCount: number;
  // NOTE: 'internal' is a superuser/support tier (not shown on public pricing),
  // but it can exist in the DB and the UI should not break when it does.
  plan: 'light' | 'individual' | 'plus' | 'pro' | 'scale' | 'team' | 'enterprise' | 'internal';

  // Addon (enricher) subscriptions stacked on top of the base tier.
  enricherSubscriptions: EnricherSubscription[];
}

interface EntitlementsContextType {
  entitlements: Entitlements | null;
  isLoading: boolean;
  hasModule: (moduleKey: string) => boolean;
  hasPlaybook: (playbookKey: string) => boolean;
  canPerformAction: (action: 'step' | 'search', count?: number) => boolean;
  hasEnricherAccess: (enricherId: string) => boolean;
  refreshEntitlements: () => Promise<void>;
  allTiers: SubscriptionTier[];
}

const EntitlementsContext = createContext<EntitlementsContextType | undefined>(undefined);

export const useEntitlements = () => {
  const context = useContext(EntitlementsContext);
  if (!context) {
    throw new Error('useEntitlements must be used within an EntitlementsProvider');
  }
  return context;
};

interface EntitlementsProviderProps {
  children: ReactNode;
}

// ============================================
// PROVIDER
// ============================================

export const EntitlementsProvider: React.FC<EntitlementsProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
  const [allTiers, setAllTiers] = useState<SubscriptionTier[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch all available tiers (for pricing page)
  const fetchTiers = async () => {
    try {
      const { data: tiers, error } = await supabase
        .from('subscription_tiers')
        .select('*')
        .eq('is_active', true)
        .order('display_order');
      
      if (!error && tiers) {
        setAllTiers(tiers);
      }
    } catch (e) {
      console.error('[Entitlements] Error fetching tiers:', e);
    }
  };

  const fetchEntitlements = async () => {
    if (!user) {
      setEntitlements(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Fetch ALL active subscriptions for this workspace (base tier + addons).
      // We can't use .maybeSingle() anymore because a workspace may have multiple
      // active rows once enricher addons start stacking on top of the base tier.
      // Split them in code: non-enricher_* prefix → base; enricher_* → addons.
      let subscriptionQuery = supabase
        .from('workspace_subscriptions')
        .select(`
          *,
          subscription_tiers (*)
        `)
        .eq('status', 'active');

      if (currentWorkspace?.id) {
        subscriptionQuery = subscriptionQuery.eq('workspace_id', currentWorkspace.id);
      } else {
        subscriptionQuery = subscriptionQuery.eq('user_id', user.id).is('workspace_id', null);
      }

      const { data: allSubs, error: subError } = await subscriptionQuery;

      const isEnricherTier = (tierId: string | null | undefined): boolean =>
        typeof tierId === 'string' && tierId.startsWith('enricher_');

      const baseSubRow = (allSubs ?? []).find((s) => !isEnricherTier(s.tier_id)) ?? null;
      const addonSubRows = (allSubs ?? []).filter((s) => isEnricherTier(s.tier_id));

      const enricherSubscriptions: EnricherSubscription[] = addonSubRows.map((row) => ({
        id: row.id,
        tier_id: row.tier_id,
        status: row.status,
        stripe_subscription_id: row.stripe_subscription_id,
        current_period_start: row.current_period_start,
        current_period_end: row.current_period_end,
        tier_name: row.subscription_tiers?.name ?? null,
        monthly_price_usd: row.subscription_tiers?.price_usd_monthly ?? null,
      }));

      // If no subscription found, use default (light tier)
      let subscription: Subscription | null = null;
      let tier: SubscriptionTier | null = null;
      let usage: SubscriptionUsage | null = null;

      const subscriptionData = baseSubRow;
      if (subscriptionData && !subError) {
        // Compute trial days remaining if on trial
        let trialDaysRemaining = 0;
        if (subscriptionData.is_trial && subscriptionData.trial_ends_at) {
          const trialEnd = new Date(subscriptionData.trial_ends_at);
          const now = new Date();
          trialDaysRemaining = Math.max(0, Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
        }
        
        subscription = {
          id: subscriptionData.id,
          tier_id: subscriptionData.tier_id,
          status: subscriptionData.status,
          stripe_customer_id: subscriptionData.stripe_customer_id,
          stripe_subscription_id: subscriptionData.stripe_subscription_id,
          current_period_start: subscriptionData.current_period_start,
          current_period_end: subscriptionData.current_period_end,
          seat_count: subscriptionData.seat_count,
          leader_count: subscriptionData.leader_count || 2,
          is_trial: subscriptionData.is_trial || false,
          trial_ends_at: subscriptionData.trial_ends_at,
          trial_days_remaining: trialDaysRemaining,
        };
        tier = subscriptionData.subscription_tiers;

        // Fetch usage for current period
        const { data: usageData } = await supabase
          .from('subscription_usage')
          .select('*')
          .eq('subscription_id', subscription.id)
          .gte('period_end', new Date().toISOString())
          .lte('period_start', new Date().toISOString())
          .maybeSingle();

        if (usageData) {
          usage = {
            steps_used: usageData.steps_used,
            searches_used: usageData.searches_used,
            protocols_active: usageData.protocols_active,
            tracks_active: usageData.tracks_active,
            period_start: usageData.period_start,
            period_end: usageData.period_end,
          };
        }
      }

      // Default to light tier if no subscription
      if (!tier) {
        const { data: lightTier } = await supabase
          .from('subscription_tiers')
          .select('*')
          .eq('id', 'light')
          .single();
        
        tier = lightTier || {
          id: 'light',
          name: 'Light',
          // New mission-based limits
          missions_limit: 3,
          concurrent_missions_limit: 1,
          credits_limit: 50,
          // Legacy limits
          steps_per_month: 5,
          searches_per_month: 20,
          protocols_limit: 1,
          tracks_limit: 1,
          connectors_limit: 2,
          memory_days: 7,
          foreach_batch_limit: 10,
          max_users: 1,
          included_users: 1,
          whatsapp_enabled: true,
          gsuite_read_only: true,
          gsuite_write: false,
          custom_connectors: false,
          sdk_access: false,
          priority_support: false,
          available_on_ios: true,
          available_on_web: true,
        } as SubscriptionTier;
      }

      // Ensure tier is not null (should never happen after the fallback above)
      if (!tier) {
        console.error('[Entitlements] Tier is unexpectedly null');
        return;
      }

      // Compute legacy limits and remaining
      const stepsLimit = tier.steps_per_month;
      const stepsUsed = usage?.steps_used || 0;
      const stepsRemaining = stepsLimit === -1 ? Infinity : Math.max(0, stepsLimit - stepsUsed);
      
      const searchesLimit = tier.searches_per_month;
      const searchesUsed = usage?.searches_used || 0;
      const searchesRemaining = searchesLimit === -1 ? Infinity : Math.max(0, searchesLimit - searchesUsed);

      // NEW: Compute mission-based limits (primary pricing model)
      const isTeamTier = tier.id === 'team';
      // Use seat_count (set by Stripe webhook) as the multiplier; fall back to legacy leader_count
      const seatMultiplier = subscription?.seat_count || subscription?.leader_count || 3;
      
      // For team tier, multiply limits by seat count (pooled across workspace)
      let missionsPerMonth = tier.missions_limit ?? 3;
      let concurrentMissions = tier.concurrent_missions_limit ?? 1;
      let creditsLimit = tier.credits_limit ?? 50;
      
      if (isTeamTier && missionsPerMonth !== -1) {
        missionsPerMonth = missionsPerMonth * seatMultiplier;
      }
      if (isTeamTier && concurrentMissions !== -1) {
        concurrentMissions = concurrentMissions * seatMultiplier;
      }
      if (isTeamTier && creditsLimit !== -1) {
        creditsLimit = creditsLimit * seatMultiplier;
      }

      // Mission usage for current month (primary pricing display metric)
      const monthStartIso = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
      let missionsUsed = 0;
      if (currentWorkspace?.id) {
        const { count } = await supabase
          .from('missions')
          .select('id', { count: 'exact', head: true })
          .eq('workspace_id', currentWorkspace.id)
          .gte('created_at', monthStartIso)
          .neq('status', 'archived');
        missionsUsed = count || 0;
      }
      const missionsRemaining = missionsPerMonth === -1
        ? Infinity
        : Math.max(0, missionsPerMonth - missionsUsed);

      // Current running missions (for concurrent limit visibility in billing UI)
      let concurrentRunning = 0;
      if (currentWorkspace?.id) {
        const { count } = await supabase
          .from('missions')
          .select('id', { count: 'exact', head: true })
          .eq('workspace_id', currentWorkspace.id)
          // Mission status vocabulary is canonical (active/awaiting/paused/completed/archived/scheduled).
          // Do not query deprecated graph-run statuses or non-existent archived_at column.
          .in('status', ['active', 'awaiting']);
        concurrentRunning = count || 0;
      }

      // Credits usage for current cycle (from dedicated credits ledger)
      let creditsUsed = 0;
      let creditsRemaining = creditsLimit === -1 ? Infinity : creditsLimit;
      if (currentWorkspace?.id) {
        const { data: workspaceCredits } = await supabase
          .from('workspace_credits')
          .select('balance, monthly_allocation, used_this_cycle')
          .eq('workspace_id', currentWorkspace.id)
          .maybeSingle();

        if (workspaceCredits) {
          const allocated = workspaceCredits.monthly_allocation ?? creditsLimit;
          creditsLimit = allocated === null || allocated === undefined ? creditsLimit : allocated;
          creditsUsed = workspaceCredits.used_this_cycle || 0;
          creditsRemaining = workspaceCredits.balance ?? Math.max(0, creditsLimit - creditsUsed);
        }
      }

      // Build modules list based on tier features (legacy compatibility)
      const modules = ['coordination']; // Always included
      if (tier.gsuite_write) modules.push('founder', 'engineering', 'product');
      if (tier.custom_connectors) modules.push('sales', 'finance');
      if (tier.sdk_access) modules.push('enterprise');

      // Build playbooks based on tier
      const playbooks = ['daily-standup', 'meeting_action_summary'];
      if (tier.gsuite_write) {
        playbooks.push('auto-unblock', 'project-kickoff', 'team-retro');
      }
      if (tier.priority_support) {
        playbooks.push('weekly_founder_memo', 'check_in_digest');
      }

      setEntitlements({
        subscription,
        tier,
        usage,
        // NEW: Mission-based limits
        missionsPerMonth,
        missionsUsed,
        missionsRemaining,
        concurrentMissions,
        concurrentRunning,
        isTeamTier,
        leaderCount: seatMultiplier,
        isTrial: subscription?.is_trial || false,
        trialEndsAt: subscription?.trial_ends_at || null,
        // Legacy limits
        stepsLimit,
        stepsUsed,
        stepsRemaining,
        searchesLimit,
        searchesUsed,
        searchesRemaining,
        creditsLimit,
        creditsUsed,
        creditsRemaining,
        canWriteGsuite: tier.gsuite_write,
        canUseCustomConnectors: tier.custom_connectors,
        canUseSdk: tier.sdk_access,
        hasPrioritySupport: tier.priority_support,
        modules,
        playbooks,
        seatCount: subscription?.seat_count || 1,
        plan: (tier.id as Entitlements['plan']) || 'light',
        enricherSubscriptions,
      });

    } catch (error) {
      console.error('[Entitlements] Error:', error);
      // Fallback to minimal entitlements (free tier defaults)
      setEntitlements({
        subscription: null,
        tier: null,
        usage: null,
        // Mission-based limits (free tier)
        missionsPerMonth: 3,
        missionsUsed: 0,
        missionsRemaining: 3,
        concurrentMissions: 1,
        concurrentRunning: 0,
        isTeamTier: false,
        leaderCount: 0,
        isTrial: false,
        trialEndsAt: null,
        // Legacy limits
        stepsLimit: 5,
        stepsUsed: 0,
        stepsRemaining: 5,
        searchesLimit: 20,
        searchesUsed: 0,
        searchesRemaining: 20,
        creditsLimit: 50,
        creditsUsed: 0,
        creditsRemaining: 50,
        canWriteGsuite: false,
        canUseCustomConnectors: false,
        canUseSdk: false,
        hasPrioritySupport: false,
        modules: ['coordination'],
        playbooks: [],
        seatCount: 1,
        plan: 'light',
        enricherSubscriptions: [],
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTiers();
  }, []);

  useEffect(() => {
    if (user) {
      fetchEntitlements();
    } else {
      setEntitlements(null);
      setIsLoading(false);
    }
  }, [user, currentWorkspace?.id]);

  // Legacy compatibility
  const hasModule = (moduleKey: string): boolean => {
    return entitlements?.modules.includes(moduleKey) || false;
  };

  const hasPlaybook = (playbookKey: string): boolean => {
    return entitlements?.playbooks.includes(playbookKey) || false;
  };

  // New usage-based check
  const canPerformAction = (action: 'step' | 'search', count = 1): boolean => {
    if (!entitlements) return false;

    if (action === 'step') {
      return entitlements.stepsLimit === -1 || entitlements.stepsRemaining >= count;
    }
    if (action === 'search') {
      return entitlements.searchesLimit === -1 || entitlements.searchesRemaining >= count;
    }
    return false;
  };

  // Does the current workspace have an active subscription for a given enricher?
  // enricherId is the addon tier_id, e.g. 'enricher_proxycurl_person_starter'.
  const hasEnricherAccess = (enricherId: string): boolean => {
    if (!entitlements) return false;
    return entitlements.enricherSubscriptions.some(
      (s) => s.tier_id === enricherId && s.status === 'active',
    );
  };

  const refreshEntitlements = useCallback(async () => {
    await fetchEntitlements();
  }, [user, currentWorkspace?.id]);

  const value: EntitlementsContextType = {
    entitlements,
    isLoading,
    hasModule,
    hasPlaybook,
    canPerformAction,
    hasEnricherAccess,
    refreshEntitlements,
    allTiers,
  };

  return (
    <EntitlementsContext.Provider value={value}>
      {children}
    </EntitlementsContext.Provider>
  );
};
